/**
 * executionBlockService.ts
 * CRUD + load/save para modelo block-based de execução.
 */
import { supabase } from '../../../lib/supabaseClient';
import type {
  ExecutionBlock,
  ExecutionBlockType,
  ExecutionBlockConfig,
  FieldBlockConfig,
  ChecklistBlockConfig,
  EvidenceBlockConfig,
  RuleBlockConfig,
} from '../../../types/card';

// ============================================================================
// LOAD BLOCKS (hierárquico)
// ============================================================================

export async function loadStageBlocks(
  stageId: string,
  organizationId: string,
): Promise<ExecutionBlock[]> {
  const { data, error } = await supabase
    .from('execution_blocks_v1')
    .select('*')
    .eq('stage_id', stageId)
    .eq('organization_id', organizationId)
    .order('order_index', { ascending: true });

  if (error) {
    console.error('[blocks] loadStageBlocks error:', error.message);
    return [];
  }

  return buildTree(
    (data ?? []).map(mapRow),
  );
}

function mapRow(row: any): ExecutionBlock {
  return {
    id: row.id,
    organizationId: row.organization_id,
    stageId: row.stage_id,
    blueprintId: row.blueprint_id ?? undefined,
    type: row.type as ExecutionBlockType,
    label: row.label ?? '',
    orderIndex: row.order_index,
    parentId: row.parent_id ?? null,
    config: row.config ?? {},
  };
}

function buildTree(flat: ExecutionBlock[]): ExecutionBlock[] {
  const map = new Map<string, ExecutionBlock>();
  const roots: ExecutionBlock[] = [];

  for (const block of flat) {
    block.children = [];
    map.set(block.id, block);
  }

  for (const block of flat) {
    if (block.parentId && map.has(block.parentId)) {
      map.get(block.parentId)!.children!.push(block);
    } else {
      roots.push(block);
    }
  }

  return roots;
}

// ============================================================================
// SAVE BLOCKS (full replace strategy)
// ============================================================================

export async function saveStageBlocks(
  stageId: string,
  organizationId: string,
  blocks: ExecutionBlock[],
): Promise<void> {
  // Flatten tree
  const rows = flattenBlocks(blocks, null);

  // Delete existing blocks for this stage
  const { error: delError } = await supabase
    .from('execution_blocks_v1')
    .delete()
    .eq('stage_id', stageId)
    .eq('organization_id', organizationId);

  if (delError) {
    console.error('[blocks] delete error:', delError.message);
    return;
  }

  if (rows.length === 0) return;

  const { error: insError } = await supabase
    .from('execution_blocks_v1')
    .insert(rows.map(b => ({
      id: b.id,
      organization_id: organizationId,
      stage_id: stageId,
      blueprint_id: b.blueprintId ?? null,
      type: b.type,
      label: b.label,
      order_index: b.orderIndex,
      parent_id: b.parentId ?? null,
      config: b.config,
    })));

  if (insError) {
    console.error('[blocks] insert error:', insError.message);
  }
}

function flattenBlocks(blocks: ExecutionBlock[], parentId: string | null): ExecutionBlock[] {
  const result: ExecutionBlock[] = [];
  for (let i = 0; i < blocks.length; i++) {
    const block = { ...blocks[i], parentId, orderIndex: i };
    result.push(block);
    if (block.children?.length) {
      result.push(...flattenBlocks(block.children, block.id));
    }
  }
  return result;
}

// ============================================================================
// RESPONSES — load/save por task
// ============================================================================

export async function loadBlockResponses(
  taskId: string,
  organizationId: string,
): Promise<Record<string, unknown>> {
  const { data, error } = await supabase
    .from('execution_block_responses_v1')
    .select('block_id, value')
    .eq('task_id', taskId)
    .eq('organization_id', organizationId);

  if (error) {
    console.error('[blocks] loadBlockResponses error:', error.message);
    return {};
  }

  const map: Record<string, unknown> = {};
  for (const row of data ?? []) {
    map[row.block_id] = row.value;
  }
  return map;
}

export async function saveBlockResponse(
  taskId: string,
  organizationId: string,
  blockId: string,
  value: unknown,
  userId: string,
): Promise<void> {
  const { error } = await supabase
    .from('execution_block_responses_v1')
    .upsert({
      task_id: taskId,
      organization_id: organizationId,
      block_id: blockId,
      value,
      completed_by: userId,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'task_id,block_id' });

  if (error) {
    console.error('[blocks] saveBlockResponse error:', error.message);
  }
}

export async function saveAllBlockResponses(
  taskId: string,
  organizationId: string,
  responses: Record<string, unknown>,
  userId: string,
): Promise<void> {
  const rows = Object.entries(responses).map(([blockId, value]) => ({
    task_id: taskId,
    organization_id: organizationId,
    block_id: blockId,
    value,
    completed_by: userId,
    updated_at: new Date().toISOString(),
  }));

  if (rows.length === 0) return;

  const { error } = await supabase
    .from('execution_block_responses_v1')
    .upsert(rows, { onConflict: 'task_id,block_id' });

  if (error) {
    console.error('[blocks] saveAllBlockResponses error:', error.message);
  }
}

// ============================================================================
// MIGRATION FALLBACK — convert v2 config to blocks
// ============================================================================

export function convertV2ToBlocks(
  stageId: string,
  organizationId: string,
  v2Config: {
    fields?: any[];
    checklist?: any[];
    rules?: any[];
    requireAttachment?: boolean;
    requireJustification?: boolean;
    requirePhoto?: boolean;
    instruction?: string;
  },
): ExecutionBlock[] {
  const blocks: ExecutionBlock[] = [];
  let idx = 0;

  // Group fields by groupName
  const fieldsByGroup = new Map<string, any[]>();
  for (const f of v2Config.fields ?? []) {
    const g = f.groupName ?? f.group_name ?? '';
    if (!fieldsByGroup.has(g)) fieldsByGroup.set(g, []);
    fieldsByGroup.get(g)!.push(f);
  }

  for (const [groupName, fields] of fieldsByGroup.entries()) {
    const groupId = crypto.randomUUID();
    const groupBlock: ExecutionBlock = {
      id: groupId,
      organizationId,
      stageId,
      type: 'group',
      label: groupName || 'Campos',
      orderIndex: idx++,
      parentId: null,
      config: {},
      children: [],
    };

    for (let fi = 0; fi < fields.length; fi++) {
      const f = fields[fi];
      const fieldConfig: FieldBlockConfig = {
        fieldType: f.type,
        required: f.required ?? false,
        placeholder: f.placeholder,
        description: f.description,
        defaultValue: f.defaultValue ?? f.default_value,
        options: f.options?.map((o: any) =>
          typeof o === 'string' ? { value: o, label: o } : o,
        ),
        validation: f.validation ?? f.validation_rules,
      };

      groupBlock.children!.push({
        id: f.id ?? crypto.randomUUID(),
        organizationId,
        stageId,
        type: 'field',
        label: f.label,
        orderIndex: fi,
        parentId: groupId,
        config: fieldConfig,
      });
    }

    blocks.push(groupBlock);
  }

  // Checklist
  if (v2Config.checklist?.length) {
    const checklistId = crypto.randomUUID();
    const items = v2Config.checklist.map((c: any) => ({
      id: c.id ?? crypto.randomUUID(),
      label: c.label,
      required: c.required !== false,
    }));

    blocks.push({
      id: checklistId,
      organizationId,
      stageId,
      type: 'checklist',
      label: 'Checklist',
      orderIndex: idx++,
      parentId: null,
      config: {
        items,
        requireAll: true,
        requireOrder: false,
      } as ChecklistBlockConfig,
    });
  }

  // Evidence
  if (v2Config.requireAttachment || v2Config.requireJustification || v2Config.requirePhoto) {
    blocks.push({
      id: crypto.randomUUID(),
      organizationId,
      stageId,
      type: 'evidence',
      label: 'Evidência',
      orderIndex: idx++,
      parentId: null,
      config: {
        requireAttachment: v2Config.requireAttachment ?? false,
        requireJustification: v2Config.requireJustification ?? false,
        requirePhoto: v2Config.requirePhoto ?? false,
        multiple: true,
      } as EvidenceBlockConfig,
    });
  }

  // Rules
  for (const r of v2Config.rules ?? []) {
    const cond = r.condition ?? {};
    blocks.push({
      id: r.id ?? crypto.randomUUID(),
      organizationId,
      stageId,
      type: 'rule',
      label: r.message ?? 'Regra',
      orderIndex: idx++,
      parentId: null,
      config: {
        condition: {
          fieldBlockId: cond.fieldId ?? '',
          operator: cond.operator ?? 'equals',
          value: cond.value,
        },
        action: r.rule_type ?? r.action ?? 'show_warning',
        message: r.message ?? '',
        type: r.type ?? 'error',
      } as RuleBlockConfig,
    });
  }

  return blocks;
}
